import { DEFAULT_LAYOUT_ID } from '@src/components/AppLayout/Layouts/tokens';
import { useActivateDealMutation } from '@src/components/OMS/ManualDealTradeView/endpoints/activateDealEndpoint';
import { getDealCashflowsRoute } from '@src/containers/Routes/routes';
import { useFeatureFlag } from '@src/hooks/useFeatureFlag';
import { useRoleAuth } from '@src/hooks/useRoleAuth';
import type { OTCDealData } from '@src/types/OTC';
import { isFixedRateLoanDeal, isFloatingRateLoanDeal } from '@src/types/OTC/otcGuards';
import { isDealActivatable, isDealCancelable, isDealModifiable } from '@src/utils/OTC/otcUtils';
import {
  ACTION,
  agGridMenuSeparator,
  filterByCellValueMenuItem,
  IconName,
  useCallbackRef,
  useJsonModal,
  useMixpanel,
  useSecuritiesContext,
  type AgGridIconButtonProps,
  type AgGridMenuItems,
  type Column,
  type ColumnDef,
  type FilterableProperty,
  type FilterBuilderSide,
  type Security,
} from '@talos/kyoko';
import type { GetContextMenuItemsParams, MenuItemDef } from 'ag-grid-community';
import { compact } from 'lodash-es';
import { useMemo, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCancelDealDialog } from '../CancelDealDialog';
import { createDealJsonObject, getDealJsonObject } from '../dealJsonObject';
import { useModifyDealMenuAction } from '../useModifyDealMenuAction';
import { viewDealScheduleMenuItem } from '../viewDealScheduleMenuItem';
import { LoanGridRowMenu } from './LoanGridRowMenu';
import { colIDToFilterBuilderKey } from './useLoanFilter';
interface UseLoanMenuParams {
  openClause?: (property: string | undefined, side?: FilterBuilderSide, value?: string | string[]) => void;
  filterableProperties?: FilterableProperty<string>[];
  /** Opens loan instrument inspector; must be stable (e.g. from parent `useCallback`). */
  openLoanInstrumentInspectorDrawer: (deal: OTCDealData, onlyIfOpen?: boolean) => void;
  layoutId?: string;
}

export function useLoanMenu({
  openClause,
  filterableProperties,
  openLoanInstrumentInspectorDrawer,
  layoutId,
}: UseLoanMenuParams): {
  columns: Column[];
  getCustomContextMenuItems: (params: GetContextMenuItemsParams<OTCDealData>) => AgGridMenuItems;
  dataDisplayCallback: (data: OTCDealData) => unknown;
  dialogs: ReactNode;
} {
  const { securitiesBySymbol } = useSecuritiesContext();
  const { handleModifyDeal, modifyDealDialog } = useModifyDealMenuAction();
  const { handleClickJson, jsonModal } = useJsonModal<{ loan: OTCDealData; security: Security | undefined }>();
  const handleCancelDeal = useCancelDealDialog();
  const [activateDeal] = useActivateDealMutation();
  const mixpanel = useMixpanel();
  const { isAuthorized } = useRoleAuth();
  const { enableDealCashflows } = useFeatureFlag();
  const navigate = useNavigate();
  const canSubmitDeal = isAuthorized(ACTION.SUBMIT_DEAL);
  const canCancelDeal = isAuthorized(ACTION.CANCEL_DEAL);

  const handleViewSchedule = useCallbackRef((dealID: string) => {
    navigate(getDealCashflowsRoute({ dealID, layoutId: layoutId ?? DEFAULT_LAYOUT_ID }));
  });

  const createCustomJsonObject = useCallbackRef((loan?: OTCDealData) => {
    createDealJsonObject({
      deal: loan,
      propertyName: 'loan',
      securitiesBySymbol,
      handleClickJson,
    });
  });

  const dataDisplayCallback = useCallbackRef((loan: OTCDealData) =>
    getDealJsonObject({
      deal: loan,
      propertyName: 'loan',
      securitiesBySymbol,
    })
  );

  const getCustomContextMenuItems = useCallbackRef((params: GetContextMenuItemsParams<OTCDealData>) => {
    const data = params.node?.data;

    const isModifiable = data && isDealModifiable(data);
    const isCancelable = data && isDealCancelable(data);
    const canModifyLoan = data && isModifiable && (isFixedRateLoanDeal(data) || isFloatingRateLoanDeal(data));
    const canActivateLoan = data && isDealActivatable(data) && data.ID != null;

    const filterItems: AgGridMenuItems = [];
    if (openClause) {
      const filterMenuItems = filterByCellValueMenuItem({
        params,
        filterableProperties: filterableProperties || [],
        openClause,
        colIDToFilterBuilderKey,
        mixpanel,
      });
      filterItems.push(
        ...filterMenuItems.filter(
          (item): item is MenuItemDef<OTCDealData> => item !== agGridMenuSeparator && item !== 'separator'
        )
      );
    }
    const menuItems: AgGridMenuItems = compact([
      ...filterItems,
      ...(filterItems.length > 0 ? [agGridMenuSeparator] : []),
      {
        name: 'View Instrument Details',
        action: () => {
          if (data) {
            openLoanInstrumentInspectorDrawer(data);
          }
        },
        icon: `<i class="ag-icon ${IconName.InformationCircle}"/>`,
      },
      viewDealScheduleMenuItem({
        deal: data,
        enabled: enableDealCashflows,
        name: 'View Loan Schedule',
        onViewSchedule: handleViewSchedule,
      }),
    ]);

    const secondaryActions: MenuItemDef[] = [];
    if (canModifyLoan && canSubmitDeal) {
      secondaryActions.push({
        name: 'Modify Loan',
        action: () => {
          if (data) {
            handleModifyDeal(data);
          }
        },
        icon: `<i class="ag-icon ${IconName.Pencil}"/>`,
      });
    }
    if (isCancelable && canCancelDeal) {
      secondaryActions.push({
        name: 'Cancel Loan',
        action: () => {
          handleCancelDeal(data);
        },
        icon: `<i class="ag-icon ${IconName.Trash}"/>`,
      });
    }
    if (canActivateLoan && canSubmitDeal) {
      secondaryActions.push({
        name: 'Activate Loan',
        action: () => {
          if (data?.ID != null) {
            void activateDeal({ dealId: data.ID });
          }
        },
        icon: `<i class="ag-icon ${IconName.CheckCircle}"/>`,
      });
    }
    if (secondaryActions.length > 0) {
      menuItems.push(agGridMenuSeparator, ...secondaryActions);
    }

    return menuItems;
  });

  const columns = useMemo(
    () =>
      [
        // Show More Details button (pinned to the right, before hamburger menu)
        {
          type: 'iconButton',
          id: 'show-more-details',
          frozen: true,
          pinned: 'right',
          suppressColumnsToolPanel: true,
          width: 40,
          params: {
            icon: IconName.Deepdive,
            onClick: params => {
              const data: OTCDealData | undefined = params.node.data;
              if (data) {
                openLoanInstrumentInspectorDrawer(data);
              }
            },
          } satisfies AgGridIconButtonProps,
        },
        {
          type: 'hamburgerMenu',
          id: 'loanMenu',
          width: 40,
          params: {
            renderItems: params => <LoanGridRowMenu {...params} onShowJson={createCustomJsonObject} />,
          },
        },
      ] satisfies ColumnDef<OTCDealData>[],
    [createCustomJsonObject, openLoanInstrumentInspectorDrawer]
  );

  const dialogs = useMemo(
    () => (
      <>
        {jsonModal}
        {modifyDealDialog}
      </>
    ),
    [jsonModal, modifyDealDialog]
  );
  return {
    columns,
    getCustomContextMenuItems,
    dataDisplayCallback,
    dialogs,
  };
}
